import { UserButton, UserProfilePage, useClerk } from '../lib/auth';
import type { UserButtonProps } from '../lib/auth';
import { useClerkChunkFailure } from '../lib/clerkChunkFailure';
import { AccountBillingPanel } from './billing/AccountBillingPanel';
import { HugeiconsIcon } from '@hugeicons/react';
import { CreditCardIcon, Key01Icon } from '@hugeicons/core-free-icons';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { usePersonalSubscription } from '../hooks/usePersonalSubscription';
import { AccountSignInPanel } from './account/AccountSignInPanel';

/**
 * The account-menu control in the dashboard header: `<UserButton>` in the
 * common case, a plain "Sign out" button once its clerk-js UI chunk is known
 * to have failed to load.
 *
 * `<UserButton>` is the ONLY sign-out control in the dashboard shell, and
 * Clerk mounts nothing when its chunk fails — no error, no placeholder,
 * nothing to click. See `useClerkChunkFailure` for the incident and the
 * detection mechanism (a chunk failure is masked to an opaque "Script error."
 * on every `window` listener; detection has to key on the failing element,
 * not the event's message).
 */
export function UserMenuButton({
  afterSignOutUrl,
  appearance,
  userProfileProps,
  canManageSubscription = false,
  purchaseThroughClerk = true,
}: {
  afterSignOutUrl: string;
  canManageSubscription?: boolean;
  /**
   * False once checkout is Stripe's hosted page (2026-09-18). Closes Clerk's
   * purchase buttons for OWNERS too, exactly as they are closed for members,
   * and keeps Manage and Cancel: an existing Clerk payer still needs them.
   */
  purchaseThroughClerk?: boolean;
  appearance?: UserButtonProps['appearance'];
  /**
   * Props for the <UserProfile> modal that "Manage account" opens. It is a
   * separate Clerk component and takes its own `appearance`; the button's
   * `appearance` above styles only the button and its dropdown.
   */
  userProfileProps?: UserButtonProps['userProfileProps'];
}) {
  const chunkFailed = useClerkChunkFailure();
  const personalSubscription = usePersonalSubscription();
  const { signOut } = useClerk();
  const [signingOut, setSigningOut] = useState(false);
  const signOutPending = useRef(false);
  const handleSignOut = async () => {
    if (signOutPending.current) return;
    signOutPending.current = true;
    setSigningOut(true);
    try {
      await signOut({ redirectUrl: afterSignOutUrl });
    } catch {
      toast.error("Couldn't sign out. Please try again.");
    } finally {
      signOutPending.current = false;
      setSigningOut(false);
    }
  };
  // Clerk's personal subscription chooser does not know our workspace roles.
  // Layout supplies the active membership role from auth-bootstrap.service.ts
  // (mapMembershipToSessionTeam). Keep statements/payment methods/cancellation;
  // The paid-member-repro browser evidence shows Manage shares the same
  // appearance key as Switch plans. Match only purchase labels, leaving Manage
  // and its separate cancellation button available to an existing subscriber.
  const canPurchaseHere = canManageSubscription && purchaseThroughClerk;
  // Clerk's default personal plan is not the workspace's trial. Only hide a
  // confirmed default-only list: a member can still have a personal paid
  // subscription to cancel, and a failed read must not remove that action.
  const onlyDefaultPlan = personalSubscription.data?.subscriptionItems.every(item => item.plan.isDefault) === true;
  const accountProps = {
    ...userProfileProps,
    appearance: {
      ...userProfileProps?.appearance,
      elements: {
        ...userProfileProps?.appearance?.elements,
        profileSection__connectedAccounts: { display: 'none' },
        ...(onlyDefaultPlan ? { profileSection__subscriptionsList: { display: 'none' } } : {}),
        ...(!canPurchaseHere ? { profileSectionPrimaryButton__subscriptionsList: {
          '&:has([data-localization-key$="actionLabel__switchPlan"]), &:has([data-localization-key$="actionLabel__newSubscription"])': { display: 'none' },
        } } : {}),
      },
    },
  };

  if (chunkFailed) {
    return (
      <button
        type="button"
        onClick={() => void handleSignOut()}
        disabled={signingOut}
        aria-busy={signingOut}
        className="text-sm font-medium text-gray-700 hover:text-gray-900 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
      >
        <span className="text-cap-trim block">{signingOut ? 'Signing out...' : 'Sign out'}</span>
      </button>
    );
  }

  return (
    <>
      {/* Subscription details opens a separate Clerk drawer; UserProfile's
          appearance does not reach it (paid-member-fixed browser evidence).
          The role-scoped rule follows that portal while preserving Cancel. */}
      {!canPurchaseHere && <style>{'.cl-subscriptionDetailsActionButton { display: none; }'}</style>}
      <UserButton afterSignOutUrl={afterSignOutUrl} appearance={appearance} userProfileProps={accountProps}>
        <UserProfilePage label="account" />
        <UserProfilePage label="security" />
        <UserProfilePage label="Sign-in methods" url="sign-in-methods" labelIcon={<HugeiconsIcon icon={Key01Icon} size={16} />}>
          <AccountSignInPanel />
        </UserProfilePage>
        <UserProfilePage label="Billing" url="workspace-billing" labelIcon={<HugeiconsIcon icon={CreditCardIcon} size={16} />}>
          <AccountBillingPanel />
        </UserProfilePage>
        <UserProfilePage label="billing" />
      </UserButton>
    </>
  );
}
